import { Number, Skeleton } from "@xola/ui-kit";
import { DateFormat, formatDate, now } from "common";
import { Dayjs } from "dayjs";
import Highcharts, { SeriesOptionsType } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { flatMap, merge, sumBy } from "lodash";
import React, { useCallback } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useQuery, useQueryClient } from "react-query";
import { Product } from "types";
import shallow from "zustand/shallow";
import { useProducts } from "../../../hooks/resources/useProduct";
import { useSeller } from "../../../hooks/useAccount";
import { getCustomersReport } from "../../../reports/CustomersReport";
import { useCustomerAnalyticsFilterStore } from "../../../stores/reports/customerAnalyticsFilterStore";
import { NoTranslate } from "../../Common/NoTranslate";
import { StackedColumnOptions } from "../Analytics/Base/StackedColumnOptions";

interface DemographicBreakdownProps {
    quantity_count: { value: number };
    key: string;
}

export const CustomersChart = () => {
    const seller = useSeller();
    const getName = useProductName();
    const { products, isFetching } = useProducts();

    const [from, to] = useCustomerAnalyticsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const filters = useCustomerAnalyticsFilterStore((state) => state.filters);
    const productList: ID[] = filters.products.all
        ? products.map((product) => product.id)
        : filters.products.selected ?? [];

    const params = {
        seller,
        mode: filters.mode,
        products: productList,
        from,
        to,
        showAllProducts: filters.products.all,
    };
    const { data, isLoading } = useQuery<SeriesOptionsType[]>(["customersChart", params], async () =>
        getCustomersReport(params),
    );

    if (isLoading || isFetching) {
        return (
            <div className="w-full">
                <Skeleton height={400} />
            </div>
        );
    }

    // Property 'dates' does not exist on type 'SeriesOptionsType[]'.
    /* @ts-expect-error */
    const seriesDates: Dayjs[] = data?.dates ?? [];
    const mapDataToSeries = (data: number[], demographicBreakdown: DemographicBreakdownProps[]) => {
        return data.map((item, index) => ({
            x: now(seriesDates[index])?.valueOf(),
            y: item,
            demographicBreakdown: demographicBreakdown[index],
        }));
    };

    const series = data?.map((item: any) => {
        return {
            data: item?.data ? mapDataToSeries(item.data, item.demographicBreakdown) : [],
            name: item ? getName(item.name) : "N/A",
        };
    });

    const filterSeriesName = filters.products.all
        ? "all"
        : // case when some Products are only unchecked,these Product other then unchecked will falls into Selected data
        series?.find((seriesLocal: any) => seriesLocal.name === "selected")
        ? "selected"
        : null;

    const selectedSeriesData = filterSeriesName
        ? series?.filter((seriesLocal: any) => seriesLocal.name === filterSeriesName)
        : series;

    const chartOptions = getChartOptions(selectedSeriesData, seriesDates);
    const total = series && selectedSeriesData ? sumBy(flatMap(selectedSeriesData, "data"), "y") : 0;

    return (
        <div>
            <div className="mb-4">
                <var data-var="reservedCount">
                    <Number maximumFractionDigits={0}>{total}</Number>
                </var>{" "}
                Reserved
            </div>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>
    );
};

const useProductName = () => {
    const queryClient = useQueryClient();
    return useCallback((id: ID) => queryClient.getQueryData<Product>(["products", id])?.name ?? id, [queryClient]);
};

const getChartOptions = (series: any, seriesDates: Dayjs[]): Highcharts.Options => {
    /* @ts-expect-error */
    Highcharts.seriesTypes.line.prototype.drawLegendSymbol = Highcharts.seriesTypes.area.prototype.drawLegendSymbol;

    const firstDataPoint = seriesDates ? now(seriesDates[0]).valueOf() : 0;
    const lastDataPoint = seriesDates ? now(seriesDates[seriesDates.length - 1]).valueOf() : 0;

    const options: Highcharts.Options = {
        series,
        chart: {
            marginBottom: 100,
        },
        xAxis: {
            className: "no-translate",
            type: "datetime",
            min: firstDataPoint,
            max: lastDataPoint,
        },
        yAxis: {
            className: "no-translate",
            stackLabels: {
                enabled: false,
            },
        },
        tooltip: {
            shared: false,
            useHTML: true,
            formatter(): string {
                return renderToStaticMarkup(
                    <TooltipFormatter
                        date={this.x}
                        guestCount={this.y}
                        demographicBreakdown={(this.point as any).demographicBreakdown}
                        name={this.series.name}
                    />,
                );
            },
        },
        plotOptions: {
            series: {
                pointStart: firstDataPoint,
                pointInterval: 24 * 3600 * 1000,
            },
            column: {
                stacking: "normal",
                dataLabels: {
                    enabled: false,
                },
            },
        },
        legend: {
            borderWidth: 0,
            enabled: true,
            itemMarginTop: 3,
            layout: "horizontal",
            y: 330,
            align: "left",
            x: 10,
        },
    };

    return merge({}, StackedColumnOptions, options);
};

interface TooltipFormatterProps {
    date: number;
    guestCount: number;
    name: string;
    demographicBreakdown: DemographicBreakdownProps[];
}

const TooltipFormatter = ({ date, guestCount, demographicBreakdown, name }: TooltipFormatterProps) => {
    const formattedDate = formatDate(now(date), DateFormat.DATE);
    return (
        <div className="bg-black p-1 text-sm text-white">
            <NoTranslate as="div" className="mb-1 font-bold">
                {formattedDate}{" "}
            </NoTranslate>
            <div className="mb-1 font-bold">
                {["all", "N/A", "selected"].includes(name) ? "All Listings" : <NoTranslate>{name}</NoTranslate>}
            </div>

            <div className="grid min-w-max grid-cols-3 gap-1">
                {/* Total reserved count */}
                <div className="contents text-white">
                    <div className="col-span-2 truncate">Reserved</div>
                    <NoTranslate as="div" className="text-right">
                        {guestCount}
                    </NoTranslate>
                </div>

                {/* Each demographic count */}
                {demographicBreakdown.map((demographic) => {
                    const count = demographic.quantity_count.value;

                    return count ? (
                        <div key={`${demographic.key}`} className="contents text-gray">
                            <NoTranslate className="col-span-2 truncate">{demographic.key}</NoTranslate>
                            <div className="text-right">{count}</div>
                        </div>
                    ) : null;
                })}
            </div>
        </div>
    );
};
